import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './hooks/useAuth';

// Layout
import DashboardLayout from './components/layout/DashboardLayout';
import AdminLayout from './components/layout/AdminLayout';

// Auth pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PendingActivationPage from './pages/PendingActivationPage';

// User pages
import DashboardPage from './pages/DashboardPage';
import ReleasesPage from './pages/ReleasesPage';
import ReleaseFormPage from './pages/ReleaseFormPage';
import ReleaseDetailPage from './pages/ReleaseDetailPage';
import ArtistsPage from './pages/ArtistsPage';
import LabelsPage from './pages/LabelsPage';
import StatementsPage from './pages/StatementsPage';
import StatementDetailPage from './pages/StatementDetailPage';
import PaymentsPage from './pages/PaymentsPage';
import StreamingPage from './pages/StreamingPage';
import SupportPage from './pages/SupportPage';
import TicketDetailPage from './pages/TicketDetailPage';
import ProfilePage from './pages/ProfilePage';
import OACRequestPage from './pages/OACRequestPage';

// Admin pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminReleasesPage from './pages/admin/AdminReleasesPage';
import AdminReleaseReviewPage from './pages/admin/AdminReleaseReviewPage';
import AdminStatementsPage from './pages/admin/AdminStatementsPage';
import AdminFinancePage from './pages/admin/AdminFinancePage';
import AdminYouTubePage from './pages/admin/AdminYouTubePage';
import AdminArtistsPage from './pages/admin/AdminArtistsPage';
import AdminLabelsPage from './pages/admin/AdminLabelsPage';
import AdminReleaseCreatePage from './pages/admin/AdminReleaseCreatePage';
import AdminPaymentsPage from './pages/admin/AdminPaymentsPage';
import AdminSupportPage from './pages/admin/AdminSupportPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminThemePage from './pages/admin/AdminThemePage';
import AdminLegalPage from './pages/admin/AdminLegalPage';
import AdminLogsPage from './pages/admin/AdminLogsPage';
import AdminOACPage from './pages/admin/AdminOACPage';
import AdminTakedownsPage from './pages/admin/AdminTakedownsPage';
import AdminLabelMappingsPage from './pages/admin/AdminLabelMappingsPage';

// Legal pages (pubbliche)
import LegalPage from './pages/LegalPage';

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, isAdmin, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={{
          duration: 4000,
          style: { borderRadius: '8px', background: '#1A1A2E', color: '#fff' }
        }} />

        <Routes>
          {/* Auth routes (pubbliche) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/pending-activation" element={<PendingActivationPage />} />

          {/* Legal pages (pubbliche) */}
          <Route path="/legal/:slug" element={<LegalPage />} />

          {/* User routes (protette) */}
          <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="releases" element={<ReleasesPage />} />
            <Route path="releases/new" element={<ReleaseFormPage />} />
            <Route path="releases/:id" element={<ReleaseDetailPage />} />
            <Route path="releases/:id/edit" element={<ReleaseFormPage />} />
            <Route path="artists" element={<ArtistsPage />} />
            <Route path="labels" element={<LabelsPage />} />
            <Route path="statements" element={<StatementsPage />} />
            <Route path="statements/:id" element={<StatementDetailPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="streaming" element={<StreamingPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="support/:id" element={<TicketDetailPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="oac" element={<OACRequestPage />} />
          </Route>

          {/* Admin routes (protette + admin) */}
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="releases" element={<AdminReleasesPage />} />
            <Route path="releases/:id" element={<AdminReleaseReviewPage />} />
            <Route path="statements" element={<AdminStatementsPage />} />
            <Route path="payments" element={<AdminPaymentsPage />} />
            <Route path="finance" element={<AdminFinancePage />} />
            <Route path="youtube-adsense" element={<AdminYouTubePage />} />
            <Route path="artists" element={<AdminArtistsPage />} />
            <Route path="labels" element={<AdminLabelsPage />} />
            <Route path="releases/create" element={<AdminReleaseCreatePage />} />
            <Route path="support" element={<AdminSupportPage />} />
            <Route path="support/:id" element={<AdminSupportPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="theme" element={<AdminThemePage />} />
            <Route path="legal" element={<AdminLegalPage />} />
            <Route path="logs" element={<AdminLogsPage />} />
            <Route path="oac" element={<AdminOACPage />} />
            <Route path="takedowns" element={<AdminTakedownsPage />} />
            <Route path="label-mappings" element={<AdminLabelMappingsPage />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
